const graduationThreshold = (amount) =>
  Object.freeze({ amount, direction: "maximum" });

// Elements graduation thresholds are not ordinary strategy investment
// minimums. They mark the first dollar that belongs in the configured Path
// Strategy: Initial Stage ends at $24,999.99/$49,999.99, respectively.
// Ordinary strategy min/max validation continues to come from proposalType.limits.
export const ISP_FUNDING_THRESHOLDS = Object.freeze({
  "Elements - American Funds Focused": graduationThreshold(25000),
  "Elements - Capital Group Dedicated ETF": graduationThreshold(25000),
  "Elements - ProSelect ETP": graduationThreshold(25000),
  "Elements - ProSelect™ ETP": graduationThreshold(25000),
  "Social Impact Investing: ESG Strategy - Elements": graduationThreshold(50000),
  "Social Impact Investing: Faith Based Strategy - Elements":
    graduationThreshold(50000),
});

export const getIspFundingThreshold = (strategy) =>
  ISP_FUNDING_THRESHOLDS[String(strategy || "").trim()] || null;

const numericLimit = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const resolveStrategyInvestmentLimits = (strategy, proposalType) => {
  if (!strategy) return null;

  const isFusion = String(strategy).toLowerCase().includes("fusion");
  const configured = isFusion
    ? proposalType?.fusionLimits || proposalType?.limits
    : proposalType?.limits;
  const rule = getIspFundingThreshold(strategy);

  if (rule) {
    const configuredMin = numericLimit(configured?.min, 0);
    const configuredMax = numericLimit(configured?.max, Infinity);
    const initialStageMaximum = rule.amount - 0.01;
    return {
      min: configuredMin,
      max: Math.min(configuredMax, initialStageMaximum),
      threshold: rule,
    };
  }

  if (!configured) return null;
  const min = numericLimit(configured.min, 0);
  const max = numericLimit(configured.max, Infinity);
  if (min === 0 && max === Infinity) return null;
  return { min, max, threshold: null };
};

export const isInvestmentAmountWithinLimits = (amount, limits) =>
  !limits ||
  (Number.isFinite(amount) &&
    amount >= limits.min &&
    (limits.max === Infinity || amount <= limits.max));

export const formatFundingAmount = (amount) =>
  Number(amount).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export const getInvestmentLimitHelperText = (limits) => {
  if (!limits) return "";
  const rule = limits.threshold;
  if (rule?.direction === "maximum") {
    const maximum = `${formatFundingAmount(rule.amount)} graduation threshold`;
    return limits.min > 0
      ? `Minimum Account Balance ${formatFundingAmount(limits.min)}; ${maximum}`
      : maximum;
  }
  if (limits.max === Infinity) {
    return `Minimum Account Balance ${formatFundingAmount(limits.min)}`;
  }
  if (limits.min === 0) return `maximum ${formatFundingAmount(limits.max)}`;
  return `Minimum Account Balance ${formatFundingAmount(
    limits.min,
  )}; maximum ${formatFundingAmount(limits.max)}`;
};

export const getInvestmentLimitViolation = (strategy, amount, limits) => {
  if (!limits || !Number.isFinite(amount)) return "";
  if (amount < limits.min) {
    return `${strategy} has a minimum of ${formatFundingAmount(limits.min)}`;
  }
  if (limits.max !== Infinity && amount > limits.max) {
    if (
      limits.threshold?.direction === "maximum" &&
      amount >= limits.threshold.amount
    ) {
      return `${strategy} graduates to its Path Strategy at ${formatFundingAmount(
        limits.threshold.amount,
      )}`;
    }
    return `${strategy} has a maximum of ${formatFundingAmount(limits.max)}`;
  }
  return "";
};
